/**
 * Ritual Membership API
 * Handles API calls for ritual membership and payment operations
 */

import apiClient from "@/lib/api-client"
import type { RitualMembershipDetails } from "../types/detail.types"

// ============================================================================
// Types
// ============================================================================

export interface JoinRitualResponse {
  success: boolean
  data: null
  message: string
}

export interface LeaveRitualResponse {
  success: boolean
  message: string
}

export interface BuyRitualResponse {
  success: boolean
  data: {
    checkoutUrl: string
    sessionId: string
    amount: number
    currency: string
  }
  message: string
}

export interface VerifyPurchaseResponse {
  success: boolean
  data: {
    paymentStatus: string
    membershipCreated: boolean
  }
  message: string
}

export interface MembershipStatusResponse {
  success: boolean
  data: {
    membershipDetails: RitualMembershipDetails | null
  }
  message?: string
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Join a free ritual
 * @param ritualId - The ID of the ritual to join
 */
export const joinRitual = async (ritualId: string): Promise<JoinRitualResponse> => {
  const response = await apiClient.post<JoinRitualResponse>(`/api/rituals/${ritualId}/join`)
  return response.data
}

/**
 * Leave a ritual
 * @param ritualId - The ID of the ritual to leave
 */
export const leaveRitual = async (ritualId: string): Promise<LeaveRitualResponse> => {
  const response = await apiClient.post<LeaveRitualResponse>(`/api/rituals/${ritualId}/leave`)
  return response.data
}

/**
 * Buy a paid ritual - Create Checkout Session
 * @param ritualId - The ID of the ritual to purchase
 * @param successUrl - URL to redirect after successful payment
 * @param cancelUrl - URL to redirect after cancelled payment
 */
export const buyRitual = async (
  ritualId: string,
  successUrl: string,
  cancelUrl: string,
): Promise<BuyRitualResponse> => {
  const response = await apiClient.post<BuyRitualResponse>(
    `/api/payments/rituals/${ritualId}/buy`,
    {
      successUrl,
      cancelUrl,
    },
  )
  return response.data
}

/**
 * Get membership status (included in ritual detail response)
 * This is typically not called separately as it's included in getRitualById
 */

export default {
  joinRitual,
  leaveRitual,
  buyRitual,
}
